import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { OffDayPlanDay } from "@/lib/daycare-hotel/offDayDecision";

/**
 * Diárias da reserva guarda-chuva de uma matrícula, com id — que é o que falta
 * em `usePlanUsageDetails` e sem o qual não dá para cancelar um dia específico.
 */
export const daycarePlanDaysKey = (contractId: string | null): unknown[] => ["daycare-plan-days", contractId];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeDaycarePlanDay(row: any): OffDayPlanDay {
  return { id: row.id, date: row.stay_date, status: row.status };
}

export function useDaycarePlanDays(contractId: string | null): UseQueryResult<OffDayPlanDay[]> {
  return useQuery<OffDayPlanDay[]>({
    queryKey: daycarePlanDaysKey(contractId),
    enabled: Boolean(contractId),
    queryFn: async () => {
      if (!contractId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase as any;

      const { data: reservation, error: reservationError } = await client
        .from("stay_reservations")
        .select("id")
        .eq("daycare_plan_contract_id", contractId)
        .maybeSingle();
      if (reservationError) throw reservationError;
      if (!reservation) return [];

      const { data, error } = await client
        .from("stay_reservation_days")
        .select("id, stay_date, status")
        .eq("stay_reservation_id", reservation.id)
        .order("stay_date", { ascending: true });
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map(normalizeDaycarePlanDay);
    },
  });
}
